import { useState, useRef, useEffect, useCallback } from 'react';
import { Cpu, RefreshCw, Search, ArrowRight } from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';
import { StatusBadge } from './StatusBadge';
import { isUuid, genUuidFromSn, resolveDeviceId } from '../lib/utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DeviceMap = Record<string, any>;

interface DeviceListProps {
  devices: DeviceMap;
  cursor: string | null;
  selectedDevice: string | null;
  onSelect: (uuid: string) => void;
  onLookup: (uuid: string) => Promise<void>;
  loading: boolean;
  loadingMore: boolean;
  onRefresh: () => void;
  onLoadMore: () => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

export const DeviceList = ({ devices, cursor, selectedDevice, onSelect, onLookup, loading, loadingMore, onRefresh, onLoadMore, searchTerm, setSearchTerm }: DeviceListProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const filteredDevices = Object.entries(devices || {}).filter(([uuid, d]) => {
    if (uuid === 'cursor') return false;
    const term = searchTerm.toLowerCase().trim();
    if (!term) return true;
    if (uuid.toLowerCase().includes(term)) return true;
    const sn: string = d.sn || d.info?.[0]?.data?.SN || '';
    if (sn.toLowerCase().includes(term)) return true;
    const url: string = d.setting?.url || '';
    if (url.toLowerCase().includes(term)) return true;
    if (!isUuid(term)) {
      const generatedUuid = genUuidFromSn(term);
      if (uuid.toLowerCase() === generatedUuid.toLowerCase()) return true;
    }
    return false;
  });

  const handleScroll = useCallback(() => {
    if (!listRef.current || loadingMore || !cursor) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) onLoadMore();
  }, [cursor, loadingMore, onLoadMore]);

  useEffect(() => {
    const list = listRef.current;
    if (list) {
      list.addEventListener('scroll', handleScroll);
      return () => list.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleDirectLookup = async () => {
    if (!searchTerm.trim() || lookupLoading) return;
    setLookupLoading(true);
    const uuid = resolveDeviceId(searchTerm.trim());
    await onLookup(uuid);
    setLookupLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchTerm.trim()) handleDirectLookup();
  };

  const showDirectLookup = searchTerm.trim() && filteredDevices.length === 0;

  return (
    <Card title={`Devices (${filteredDevices.length})`} icon={Cpu} className="h-full flex flex-col">
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-2 top-2.5 text-gray-500" />
          <input
            placeholder="Search by SN, UUID, or URL..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full bg-gray-700 border border-gray-600 rounded pl-8 pr-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
          />
        </div>
        <Button onClick={onRefresh} size="md" variant="secondary" disabled={loading}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {showDirectLookup && (
        <div className="mb-3 p-3 bg-gray-700 rounded">
          <p className="text-sm text-gray-300 mb-2">Device not in list. Look up directly?</p>
          <div className="flex gap-2 items-center">
            <code className="text-xs text-blue-400 flex-1 truncate">
              {isUuid(searchTerm.trim()) ? searchTerm.trim() : `${searchTerm.trim()} → ${genUuidFromSn(searchTerm.trim()).slice(0, 8)}...`}
            </code>
            <Button size="sm" variant="primary" onClick={handleDirectLookup} disabled={lookupLoading}>
              {lookupLoading ? <RefreshCw size={14} className="animate-spin" /> : <><ArrowRight size={14} /> Lookup</>}
            </Button>
          </div>
        </div>
      )}

      <div ref={listRef} className="flex-1 overflow-auto space-y-1 min-h-0">
        {loading && Object.keys(devices).length === 0 ? (
          <div className="flex items-center justify-center py-8 text-gray-400"><RefreshCw className="animate-spin" size={20} /></div>
        ) : filteredDevices.length === 0 && !showDirectLookup ? (
          <p className="text-gray-500 text-center py-4">No devices found</p>
        ) : (
          <>
            {filteredDevices.map(([uuid, device]) => {
              const sn: string = device.sn || device.info?.[0]?.data?.SN;
              return (
                <div key={uuid} onClick={() => onSelect(uuid)} className={`p-3 rounded cursor-pointer transition-colors ${selectedDevice === uuid ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}>
                  <div className="flex justify-between items-start">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-white truncate">{sn || uuid.slice(0, 8)}</p>
                      <p className="text-xs text-gray-400 font-mono truncate">{uuid}</p>
                    </div>
                    <StatusBadge status={device.status} />
                  </div>
                  <div className="mt-2 flex gap-3 text-xs text-gray-400 flex-wrap">
                    {(device.hb?.jar || device.state?.jar) && <span>v{device.hb?.jar || device.state?.jar}</span>}
                    {(device.hb?.conn || device.state?.conn) && <span>{device.hb?.conn || device.state?.conn}</span>}
                  </div>
                </div>
              );
            })}
            {loadingMore && (
              <div className="flex items-center justify-center py-4 text-gray-400">
                <RefreshCw className="animate-spin" size={16} />
                <span className="ml-2 text-sm">Loading more...</span>
              </div>
            )}
            {cursor && !loadingMore && (
              <div className="text-center py-2">
                <Button size="sm" variant="ghost" onClick={onLoadMore}>Load more devices</Button>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
};
